import { apiFetch } from './api';
import { serverOrigin } from './server-origin';

export interface ChannelConnectionRow {
  id: string;
  siteId: string;
  channel: 'api' | 'whatsapp' | 'email' | 'instagram' | 'messenger';
  name: string;
  status: 'active' | 'disconnected' | 'error';
  outboundUrl: string | null;
  /** Email channel: the support address replies come from. */
  address: string | null;
  fromName: string | null;
  allowUnverifiedSenders: boolean;
  /** Server-relative; see webhookUrl(). */
  webhookPath: string;
  /** Instagram only: the connected account and when its token lapses. */
  instagram: { username: string | null; tokenExpiresAt: string | null } | null;
  /** Messenger only: the connected Facebook Page. */
  messenger: { pageName: string | null } | null;
  /** WhatsApp only: the business number, and the verify token to paste in Meta. */
  whatsapp: {
    phoneNumberId: string | null;
    displayPhone: string | null;
    verifiedName: string | null;
    verifyToken: string | null;
  } | null;
  /** Set while the provider's rate limit holds sends back. */
  rateLimitedUntil: string | null;
  hasSecret: boolean;
  lastHealthAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

const base = (siteId: string) => `/api/admin/sites/${siteId}/channels`;

/** The absolute URL the customer's system posts to. */
export const webhookUrl = (c: ChannelConnectionRow) => `${serverOrigin()}${c.webhookPath}`;

/** The URL to paste into SendGrid Inbound Parse: the secret rides along as
 *  basic-auth credentials, since Inbound Parse can't sign its requests. */
export function inboundParseUrl(c: ChannelConnectionRow, secret: string): string {
  const url = new URL(webhookUrl(c));
  url.username = 'openline';
  url.password = secret;
  return url.toString();
}

export type CreateChannelInput =
  | { channel: 'api'; name: string; outboundUrl?: string }
  | { channel: 'email'; name: string; address: string; fromName?: string; allowUnverifiedSenders?: boolean };

export const listChannels = (siteId: string) =>
  apiFetch<{ channels: ChannelConnectionRow[] }>(base(siteId)).then((r) => r.channels);

/** Returns the signing secret — the only time it is ever shown. */
export const createChannel = (siteId: string, input: CreateChannelInput) =>
  apiFetch<{ channel: ChannelConnectionRow; secret: string }>(base(siteId), {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const updateChannel = (
  siteId: string,
  id: string,
  patch: Partial<{
    name: string;
    outboundUrl: string;
    address: string;
    fromName: string;
    allowUnverifiedSenders: boolean;
    status: 'active' | 'disconnected';
  }>,
) =>
  apiFetch<{ channel: ChannelConnectionRow }>(`${base(siteId)}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

export const rotateChannelSecret = (siteId: string, id: string) =>
  apiFetch<{ channel: ChannelConnectionRow; secret: string }>(`${base(siteId)}/${id}/rotate-secret`, {
    method: 'POST',
  });

export const checkChannel = (siteId: string, id: string) =>
  apiFetch<{ result: { ok: boolean; detail?: string }; channel: ChannelConnectionRow }>(
    `${base(siteId)}/${id}/check`,
    { method: 'POST' },
  );

export const deleteChannel = (siteId: string, id: string) =>
  apiFetch<void>(`${base(siteId)}/${id}`, { method: 'DELETE' });

/** Whether the server has the Meta app configured (env), for the Instagram button. */
export const instagramAvailable = (siteId: string) =>
  apiFetch<{ configured: boolean; encryption: boolean }>(`${base(siteId)}/instagram/available`);

/** Instagram's consent-screen URL. Pass a connection id to reconnect it. */
export const authorizeInstagram = (siteId: string, connectionId?: string) =>
  apiFetch<{ url: string }>(`${base(siteId)}/instagram/authorize`, {
    method: 'POST',
    body: JSON.stringify(connectionId ? { connectionId } : {}),
  }).then((r) => r.url);

/** Drops the token and unsubscribes; the conversations stay. */
export const disconnectInstagram = (siteId: string, id: string) =>
  apiFetch<{ channel: ChannelConnectionRow }>(`${base(siteId)}/${id}/disconnect`, { method: 'POST' });

/** Whether the server has the Facebook app configured (env), for the Messenger button. */
export const messengerAvailable = (siteId: string) =>
  apiFetch<{ configured: boolean; encryption: boolean }>(`${base(siteId)}/messenger/available`);

/** Facebook's consent-screen URL. Pass a connection id to reconnect its Page. */
export const authorizeMessenger = (siteId: string, connectionId?: string) =>
  apiFetch<{ url: string }>(`${base(siteId)}/messenger/authorize`, {
    method: 'POST',
    body: JSON.stringify(connectionId ? { connectionId } : {}),
  }).then((r) => r.url);

/** Drops the Page token and unsubscribes; the conversations stay. */
export const disconnectMessenger = (siteId: string, id: string) =>
  apiFetch<{ channel: ChannelConnectionRow }>(`${base(siteId)}/${id}/messenger/disconnect`, { method: 'POST' });

export interface WhatsAppInput {
  name: string;
  phoneNumberId: string;
  accessToken: string;
  appSecret: string;
}

/** Connect a WhatsApp business number (Meta Cloud API). */
export const connectWhatsApp = (siteId: string, input: WhatsAppInput) =>
  apiFetch<{ channel: ChannelConnectionRow }>(`${base(siteId)}/whatsapp`, {
    method: 'POST',
    body: JSON.stringify(input),
  }).then((r) => r.channel);

/** Replace a WhatsApp connection's token / app secret. */
export const updateWhatsApp = (siteId: string, id: string, input: WhatsAppInput) =>
  apiFetch<{ channel: ChannelConnectionRow }>(`${base(siteId)}/${id}/whatsapp`, {
    method: 'PUT',
    body: JSON.stringify(input),
  }).then((r) => r.channel);
